// R115 calendar hub — OPS builder: shared helpers for the ops modules (layer
// access, fetch, the form-sheet shell, one-shot hand-offs). Feature code only
// imports ../registry, ../types and shared components — never the page.
import type { CalendarCtx, OfficeRole, ViewId } from '../registry';
import { registerBusy } from '../registry';
import { usable, byListing, type InspLayer, type InspJob, type TasksLayer, type TaskItem, type HealthLayer } from '../../../../../lib/calendarOps.mjs';

export const OFFICE: OfficeRole[] = ['admin', 'director', 'manager', 'remote_ops'];
/** View ids the presets use (sub-presets like 'ops.hk' are plain strings in the registry). */
export const views = (...ids: string[]) => ids as unknown as ViewId[];

export const insp = (ctx: CalendarCtx): InspLayer | null => usable(ctx.layer<InspLayer>('inspections'));
export const tasks = (ctx: CalendarCtx): TasksLayer | null => usable(ctx.layer<TasksLayer>('tasks'));
export const health = (ctx: CalendarCtx): HealthLayer | null => usable(ctx.layer<HealthLayer>('health'));
export const layerDown = (ctx: CalendarCtx, id: string): boolean => {
  const l = ctx.layer<{ unavailable?: boolean }>(id);
  return !!(l && l.unavailable) || !!ctx.layerError(id);
};

// Per-layer-object indexes (the timeline asks chips() once per row per draw).
const idx = new WeakMap<object, Map<string, any[]>>();
export function jobsBy(L: InspLayer): Map<string, InspJob[]> {
  let m = idx.get(L) as Map<string, InspJob[]> | undefined;
  if (!m) { m = byListing(L.jobs); idx.set(L, m); }
  return m;
}
export function tasksBy(L: TasksLayer): Map<string, TaskItem[]> {
  let m = idx.get(L) as Map<string, TaskItem[]> | undefined;
  if (!m) { m = byListing(L.items); idx.set(L, m); }
  return m;
}
export const findJob = (ctx: CalendarCtx, id: string): InspJob | null => {
  const L = insp(ctx);
  return (L && L.jobs.find(j => j.id === id)) || null;
};
/** Tasks created from this page before the next layer load (the inspector opens them at once). */
export const createdTasks = new Map<string, TaskItem>();
export const findTask = (ctx: CalendarCtx, id: string): TaskItem | null => {
  const L = tasks(ctx);
  return (L && L.items.find(t => t.id === id)) || createdTasks.get(id) || null;
};

/** Property ids in view (the search / view row filter already applied by the page). */
export const viewIds = (ctx: CalendarCtx): string[] => ctx.data.properties.map(p => p.listing_id);
export const propName = (ctx: CalendarCtx, lid: string | null | undefined): string => {
  if (!lid) return 'Office';
  const p = ctx.property(lid);
  return p ? p.name : 'Hidden home';
};

export interface Res<T = any> { ok: boolean; status: number; json: T | null; error: string }
async function send<T>(url: string, init: RequestInit): Promise<Res<T>> {
  let r: Response;
  try { r = await fetch(url, init); }
  catch { return { ok: false, status: 0, json: null, error: 'Network error — check the connection and try again.' }; }
  let j: any = null;
  try { j = await r.json(); } catch { j = null; }
  const error = r.ok ? '' : String((j && j.error) || `Request failed (${r.status})`);
  return { ok: r.ok, status: r.status, json: j, error };
}
export const postJson = <T = any>(url: string, body: unknown): Promise<Res<T>> =>
  send<T>(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
export const getJson = <T = any>(url: string, signal?: AbortSignal): Promise<Res<T>> => send<T>(url, { signal });

/** One server layer for a window, straight from the core's layers route (the day inspector / forms). */
export async function fetchLayer<T>(id: string, from: string, to: string, signal?: AbortSignal): Promise<T> {
  const r = await getJson<any>(`/api/stays/calendar/layers?l=${encodeURIComponent(id)}&from=${from}&to=${to}`, signal);
  if (!r.ok || !r.json) throw new Error(r.error || 'layer unavailable');
  const data = r.json.layers ? r.json.layers[id] : undefined;
  if (data === undefined || data === null) throw new Error((r.json.errors && r.json.errors[id]) || 'layer unavailable');
  if (data.unavailable) throw new Error('could not be read — numbers hidden, not zero');
  return data as T;
}

export function oneShot(key: string, value: string): void { try { sessionStorage.setItem(key, value); } catch { /* private mode */ } }
export function sessionGet(key: string): string { try { return sessionStorage.getItem(key) || ''; } catch { return ''; } }

// Writes in flight veto the 15-minute auto-refresh (registerBusy) — the R37 class.
let inflight = 0;
registerBusy('ops-writes', () => inflight > 0);
export async function busy<T>(fn: () => Promise<T>): Promise<T> {
  inflight++;
  try { return await fn(); } finally { inflight = Math.max(0, inflight - 1); }
}

/** The form-sheet body: scrolling fields + the sticky footer the floating pills step aside for. */
export function sheetHtml(ctx: CalendarCtx, head: string, body: string, foot: string): string {
  return `<div class="opv-sheet">${head ? `<div class="opv-sheet-head">${head}</div>` : ''}<div class="opv-sheet-body">${body}</div>`
    + `<div class="opv-msg" data-opv-msg role="status" aria-live="polite"></div>`
    + `<div class="opv-sheet-foot" data-bottom-bar>${foot}</div></div>`;
}
/** Mark the sheet dirty on the first keystroke (the page then vetoes auto-refresh). */
export function trackDirty(ctx: CalendarCtx, root: HTMLElement): void {
  const on = () => ctx.markDirty(true);
  root.addEventListener('input', on);
  root.addEventListener('change', on);
}
/** Inline status line in a sheet / popover: '' clears; html is trusted (already escaped). */
export function say(root: HTMLElement, html: string, tone: 'info' | 'bad' | 'warn' | 'ok' = 'info'): HTMLElement | null {
  const m = root.querySelector('[data-opv-msg]') as HTMLElement | null;
  if (!m) return null;
  m.className = `opv-msg${html ? ` opv-msg-${tone}` : ''}`;
  m.innerHTML = html;
  return m;
}
export const pill = (ctx: CalendarCtx, text: string, tone: string): string => `<span class="opv-pill opv-${tone}">${ctx.esc(text)}</span>`;
